import React from 'react';
import { useParams } from 'react-router-dom';
import { Spinner, Card, Badge, Select } from 'flowbite-react';
import { useDatabase } from '../../hooks/useDatabase';
import { useProduct } from '../../hooks/useProduct';
import EditableTitle from '../../components/EditableTitle';

const LIFECYCLE_STAGES = ['Concept', 'Development', 'Growth', 'Mature', 'Sunset'];

const ProductDetailPage: React.FC<{ overrideProductId?: string }> = ({ overrideProductId }) => {
  const { productId: paramId } = useParams<{ productId: string }>();
  const productId = overrideProductId || paramId;
  const { product, loading, error } = useProduct(productId);
  const { applyCommand } = useDatabase();

  const updateName = async (newName: string) => {
    if (!productId) return;
    await applyCommand({ type: 'UpdateProject', id: productId, actions: [{ type: 'SetName', name: newName }] });
  };

  const updateDescription = async (newDescription: string) => {
    if (!productId) return;
    await applyCommand({ type: 'UpdateProject', id: productId, actions: [{ type: 'SetDescription', description: newDescription || null }] });
  };

  const updateLifecycleStage = async (stage: string) => {
    if (!productId) return;
    await applyCommand({
      type: 'UpdateProject',
      id: productId,
      actions: [{ type: 'SetLifecycleStage', lifecycle_stage: stage || null }],
    });
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <Spinner size="xl" />
      </div>
    );
  }

  if (error || !product) {
    return (
      <div className="text-center py-12 text-cm-text-muted">
        {error || 'Product not found'}
      </div>
    );
  }

  return (
    <div className="max-w-4xl mx-auto space-y-6">
      <div className="flex items-center gap-3">
        <EditableTitle value={product.name} onSave={updateName} />
        {product.draft && <Badge color="warning">Draft</Badge>}
        {product.lifecycleStage && (
          <Badge color="info">{product.lifecycleStage}</Badge>
        )}
      </div>

      <Card>
        <h3 className="font-mono font-medium text-cm-text-primary mb-3">Details</h3>
        <div className="space-y-4">
          <div>
            <label className="text-sm text-cm-text-muted block mb-1">Description</label>
            <textarea
              className="w-full bg-cm-bg-elevated border border-cm-border-subtle rounded-lg p-3 text-cm-text-primary text-sm resize-y min-h-[80px]"
              value={product.description || ''}
              onChange={(e) => updateDescription(e.target.value)}
              placeholder="What is this product?"
            />
          </div>

          <div>
            <label className="text-sm text-cm-text-muted block mb-1">Lifecycle Stage</label>
            <Select
              value={product.lifecycleStage || ''}
              onChange={(e) => updateLifecycleStage(e.target.value)}
            >
              <option value="">None</option>
              {LIFECYCLE_STAGES.map((stage) => (
                <option key={stage} value={stage}>{stage}</option>
              ))}
            </Select>
          </div>

          {product.remoteReference && (
            <div>
              <label className="text-sm text-cm-text-muted block mb-1">Repository</label>
              <a
                href={product.remoteReference.url}
                target="_blank"
                rel="noopener noreferrer"
                className="text-sm text-cm-accent-blue hover:underline"
              >
                {product.remoteReference.provider}: {product.remoteReference.url}
              </a>
            </div>
          )}

          <div className="text-xs text-cm-text-muted">
            Created: {new Date(product.createdAt).toLocaleDateString()}
          </div>
        </div>
      </Card>
    </div>
  );
};

export default ProductDetailPage;
